import React from 'react';
import { Check, X, Clock, Navigation, Edit2, Trash2, ChevronDown, CheckCircle, XCircle, SkipForward } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatTime } from './RouteMapComponents';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../../../components/ui/dropdown-menu';

// Sortable Stop Item Component for drag-and-drop reordering
export function SortableStopItem({ stop, index, onNavigate, onComplete, onEdit, onDelete, onFocusOnMap, violation, autoNavigate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stop.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`p-2 rounded-lg border ${
        stop.status === 'successful'
          ? 'bg-green-500/10 border-green-500/30' 
          : stop.status === 'completed'
          ? 'bg-purple-500/10 border-purple-500/30'
          : stop.status === 'skipped'
          ? 'bg-slate-500/10 border-slate-500/30'
          : stop.status === 'unsuccessful'
          ? 'bg-red-500/10 border-red-500/30'
          : 'bg-white dark:bg-slate-800/50 border-slate-200 dark:border-slate-700/50'
      } ${isDragging ? 'shadow-lg ring-2 ring-teal-500' : ''}`}
      data-testid={`stop-item-${index}`}
    >
      <div className="flex items-center gap-2">
        {/* Drag Handle */}
        <div 
          {...attributes} 
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-0.5 hover:bg-slate-100 dark:hover:bg-slate-700/50 rounded text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 touch-none flex-shrink-0"
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
        
        {/* Stop Number/Status Icon */}
        {(() => {
          const hasTimeConstraint = stop.time_constraint_type && stop.time_constraint_type !== 'none';
          const isDone = ['successful', 'unsuccessful', 'completed', 'skipped'].includes(stop.status);
          
          // For completed stops: show just the icon with color (no background) - smaller size
          if (isDone) {
            return (
              <div 
                onClick={() => onFocusOnMap && onFocusOnMap(stop.id)}
                title="Click to show on map"
                className="flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
              >
                {stop.status === 'successful' && <CheckCircle className="h-4 w-4 text-green-500" />}
                {stop.status === 'unsuccessful' && <XCircle className="h-4 w-4 text-red-500" />}
                {stop.status === 'completed' && <Check className="h-4 w-4 text-purple-500" />}
                {stop.status === 'skipped' && <SkipForward className="h-4 w-4 text-slate-500" />}
              </div>
            );
          }
          
          // For pending stops: show number in circle
          return (
            <div 
              onClick={() => onFocusOnMap && onFocusOnMap(stop.id)}
              title="Click to show on map"
              className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 cursor-pointer hover:scale-110 transition-transform ${
                hasTimeConstraint
                  ? 'bg-amber-500/20 text-amber-400 ring-1 ring-amber-500/50'
                  : 'bg-teal-500/20 text-teal-400'
              }`}>
              {index + 1}
            </div>
          );
        })()}
        
        {/* Stop Details - Compact Layout */}
        <div className="flex-1 min-w-0">
          {/* First Row: Address + ETA/Status */}
          <div className="flex items-center justify-between gap-2">
            <p className={`text-xs font-medium truncate ${
              stop.status !== 'pending' ? 'line-through text-slate-500' : 'text-white'
            }`}>
              {stop.address}
            </p>
            {/* ETA or Completion Time */}
            {stop.status === 'pending' ? (
              stop.estimated_arrival && (
                <span className="text-[10px] text-slate-400 flex-shrink-0">
                  ETA {formatTime(stop.estimated_arrival)}
                </span>
              )
            ) : (
              <span className={`text-[10px] flex-shrink-0 ${
                stop.status === 'successful' ? 'text-green-400' : 
                stop.status === 'unsuccessful' ? 'text-red-400' : 
                stop.status === 'completed' ? 'text-purple-400' :
                'text-slate-400'
              }`}>
                {stop.status === 'successful' ? 'Served' :
                 stop.status === 'unsuccessful' ? 'Failed' :
                 stop.status === 'completed' ? 'Completed' :
                 'Skipped'}
                {stop.completed_at && ` at ${(() => {
                  const d = new Date(stop.completed_at);
                  const h = d.getHours();
                  const m = d.getMinutes();
                  const period = h >= 12 ? 'PM' : 'AM';
                  const h12 = h % 12 || 12;
                  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
                })()}`}
              </span>
            )}
          </div>
          {/* Second Row: Name/Case (if exists) */}
          {(stop.defendant_name || stop.case_number) && (
            <p className="text-[10px] text-slate-500 truncate">
              {stop.defendant_name}
              {stop.case_number && ` • Case: ${stop.case_number}`}
            </p>
          )}
          {/* Notes if exists */}
          {stop.notes && (
            <p className="text-[10px] text-amber-400 uppercase truncate">{stop.notes}</p>
          )}
        </div>
      </div>
      
      {/* Actions Row - Compact */}
      <div className="flex items-center justify-between mt-1.5 pl-8">
        <div className="flex items-center gap-1 flex-wrap">
          {/* Time constraint badge */}
          {stop.time_constraint_type && stop.time_constraint_type !== 'none' && stop.status === 'pending' && (() => {
            // Helper to convert 24h to 12h format
            const to12Hour = (timeStr) => {
              if (!timeStr) return timeStr;
              const clean = timeStr.replace(/[ap]m?\.?\s*/gi, '').trim();
              const parts = clean.split(':').map(Number);
              let h = parts[0] || 0;
              const m = parts[1] || 0;
              // Check if already has AM/PM indicator
              const hasAMPM = /[ap]m/i.test(timeStr);
              if (hasAMPM) {
                const isPM = /pm/i.test(timeStr);
                if (isPM && h < 12) h += 12;
                if (!isPM && h === 12) h = 0;
              }
              const period = h >= 12 ? 'PM' : 'AM';
              const h12 = h % 12 || 12;
              return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
            };
            
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">
                {stop.time_constraint_type === 'arrive_before' && `BEFORE ${to12Hour(stop.time_constraint_time)}`}
                {stop.time_constraint_type === 'arrive_after' && `AFTER ${to12Hour(stop.time_constraint_time)}`}
                {stop.time_constraint_type === 'arrive_between' && `${to12Hour(stop.time_window_start)} - ${to12Hour(stop.time_window_end)}`}
              </span>
            );
          })()}
          {/* Early/On-time/Late indicator for constrained stops */}
          {stop.time_constraint_type && stop.time_constraint_type !== 'none' && stop.status === 'pending' && stop.estimated_arrival && (() => {
            // Calculate status from Mapbox ETA for consistency with displayed time
            // Parse ETA
            const [etaH, etaM] = stop.estimated_arrival.split(':').map(Number);
            const etaMinutes = etaH * 60 + etaM;
            
            // Parse constraint times
            let targetStart = null;
            let targetEnd = null;
            
            const parseTime = (timeStr) => {
              if (!timeStr) return null;
              const clean = timeStr.toLowerCase().replace(/[ap]m?\.?/g, '').trim();
              const isPM = timeStr.toLowerCase().includes('p');
              const isAM = timeStr.toLowerCase().includes('a');
              const parts = clean.split(':').map(Number);
              let h = parts[0] || 0;
              const m = parts[1] || 0;
              if (isPM && h < 12) h += 12;
              if (isAM && h === 12) h = 0;
              return h * 60 + m;
            };
            
            if (stop.time_constraint_type === 'arrive_before') {
              targetEnd = parseTime(stop.time_constraint_time);
            } else if (stop.time_constraint_type === 'arrive_after') {
              targetStart = parseTime(stop.time_constraint_time);
            } else if (stop.time_constraint_type === 'arrive_between') {
              targetStart = parseTime(stop.time_window_start);
              targetEnd = parseTime(stop.time_window_end);
              
              // Handle overnight scenarios
              if (targetStart !== null && targetEnd !== null) {
                // Case 1: Window end is before window start (e.g., 11pm to 12am)
                if (targetEnd < targetStart) {
                  targetEnd += 1440; // Add 24 hours to end
                }
                
                // Case 2: ETA is late evening and window is early morning
                // (e.g., ETA 11:44pm, window 12:00am-12:01am)
                // Need to treat the window as "next day"
                if (etaMinutes > 1080 && targetStart < 360) {
                  targetStart += 1440;
                  targetEnd += 1440;
                }
              }
            }
            
            // Calculate status
            let status = null;
            let diff = 0;
            
            if (stop.time_constraint_type === 'arrive_before' && targetEnd) {
              // BEFORE X: You just need to arrive before X
              // If you're early or on time, you've met the constraint = ON TIME
              // If you're late (after X), you've violated it = LATE
              diff = targetEnd - etaMinutes;
              if (diff >= 0) {
                status = 'ontime'; // Arriving before the deadline = success
              } else {
                status = 'late';
                diff = Math.abs(diff);
              }
            } else if (stop.time_constraint_type === 'arrive_after' && targetStart) {
              // AFTER X: You must not arrive before X
              // If you arrive after X = ON TIME
              // If you arrive before X = you'll need to wait (show as "waiting")
              diff = etaMinutes - targetStart;
              if (diff >= 0) {
                status = 'ontime'; // Arriving after the time = success
              } else {
                status = 'waiting'; // Arriving before "after" time = will wait
                diff = Math.abs(diff);
              }
            } else if (stop.time_constraint_type === 'arrive_between' && targetStart && targetEnd) {
              // For "arrive between" - show accurate status
              if (etaMinutes < targetStart) {
                // Arriving early - will need to wait
                status = 'early';
                diff = targetStart - etaMinutes;
              } else if (etaMinutes > targetEnd) {
                // Arriving late - missed the window
                status = 'late';
                diff = etaMinutes - targetEnd;
              } else {
                // Arriving within the window - truly on time
                status = 'ontime';
                diff = 0;
              }
            }
            
            if (!status) return null;
            
            const absDiff = Math.abs(diff);
            const diffText = absDiff > 0 ? `${absDiff} MIN` : '';
            
            if (status === 'ontime') {
              return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-green-500/20 text-green-400">
                  ✓ ON TIME
                </span>
              );
            } else if (status === 'early') {
              return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400">
                  {diffText} EARLY
                </span>
              );
            } else if (status === 'waiting') {
              return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-amber-500/20 text-amber-400">
                  WAIT {diffText}
                </span>
              );
            } else if (status === 'late') {
              return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/20 text-red-400">
                  ⚠ {diffText} LATE
                </span>
              );
            }
            return null;
          })()}
          {/* Service time override badge */}
          {stop.service_time_minutes && stop.status === 'pending' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-purple-500/20 text-purple-400">
              <Clock className="h-2.5 w-2.5" />
              {stop.service_time_minutes} MIN
            </span>
          )}
          {/* Priority badge (Must be First / Must be Last) */}
          {stop.priority === 'first' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 font-bold">
              FIRST
            </span>
          )}
          {stop.priority === 'last' && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/20 text-blue-400 font-bold">
              LAST
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {stop.status === 'pending' ? (
            <>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 text-teal-400 hover:bg-teal-500/20" 
                onClick={() => onNavigate(stop)}
                title="Navigate"
                data-testid={`navigate-stop-${index}`}
              >
                <Navigation className="h-4 w-4" />
              </Button>
              
              {/* Status Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 px-2 text-green-400 hover:bg-green-500/10 hover:text-green-300"
                    title="Mark Status"
                    data-testid={`status-dropdown-${index}`}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    <span className="text-xs">Done</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 bg-slate-800 border-slate-700">
                  <DropdownMenuItem 
                    onClick={() => onComplete(stop.id, 'successful', autoNavigate)}
                    className="text-green-400 hover:text-green-300 hover:bg-slate-700 focus:text-green-300 focus:bg-slate-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Served
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onComplete(stop.id, 'unsuccessful', autoNavigate)}
                    className="text-red-400 hover:text-red-300 hover:bg-slate-700 focus:text-red-300 focus:bg-slate-700"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Failed
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onComplete(stop.id, 'completed', autoNavigate)}
                    className="text-purple-400 hover:text-purple-300 hover:bg-slate-700 focus:text-purple-300 focus:bg-slate-700"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Completed
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => onComplete(stop.id, 'skipped', autoNavigate)}
                    className="text-slate-400 hover:text-slate-300 hover:bg-slate-700 focus:text-slate-300 focus:bg-slate-700"
                  >
                    <SkipForward className="h-4 w-4 mr-2" />
                    Skipped
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 text-slate-400 hover:bg-slate-500/20" 
                onClick={() => onEdit(stop)}
                title="Edit"
                data-testid={`edit-stop-${index}`}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-8 w-8 p-0 text-slate-400 hover:text-red-500" 
                onClick={() => onDelete(stop.id)}
                title="Delete"
                data-testid={`delete-stop-${index}`}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            /* Status change dropdown for completed/skipped stops - no undo to pending */
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 px-2 text-xs text-slate-400"
                  data-testid={`change-status-${index}`}
                >
                  {stop.status === 'successful' ? 'Served' : 
                   stop.status === 'unsuccessful' ? 'Failed' :
                   stop.status === 'completed' ? 'Complete' :
                   stop.status === 'skipped' ? 'Skipped' : stop.status}
                  <ChevronDown className="h-3 w-3 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-36 bg-slate-800 border-slate-700">
                <DropdownMenuItem 
                  onClick={() => onComplete(stop.id, 'successful')}
                  className="text-green-400 hover:text-green-300 hover:bg-slate-700 focus:bg-slate-700 focus:text-green-300"
                >
                  <Check className="h-3.5 w-3.5 mr-2" />
                  Served
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onComplete(stop.id, 'unsuccessful')}
                  className="text-red-400 hover:text-red-300 hover:bg-slate-700 focus:bg-slate-700 focus:text-red-300"
                >
                  <X className="h-3.5 w-3.5 mr-2" />
                  Failed
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onComplete(stop.id, 'completed')}
                  className="text-purple-400 hover:text-purple-300 hover:bg-slate-700 focus:bg-slate-700 focus:text-purple-300"
                >
                  <Check className="h-3.5 w-3.5 mr-2" />
                  Complete
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onComplete(stop.id, 'skipped')}
                  className="text-slate-400 hover:text-slate-300 hover:bg-slate-700 focus:bg-slate-700 focus:text-slate-300"
                >
                  <X className="h-3.5 w-3.5 mr-2" />
                  Skipped
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </div>
  );
}

export default SortableStopItem;
